/** Create a project from its performance guarantee PDF (parsed by the API). */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../api/client";
import { keys, useMe, useOrgs } from "../api/hooks";
import type { Project } from "../api/types";
import { StageIntro } from "../components/sat";
import { ErrorNotice, Field, FileDrop, Spinner } from "../components/ui";
import { uploadFile } from "../lib/files";

export function NewProjectPage() {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const orgs = useOrgs(isAdmin);
  const [orgId, setOrgId] = useState<string>("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const targetOrg = isAdmin ? orgId : (me.data?.org?.id as string | undefined);

  async function create() {
    if (!file || !targetOrg) return;
    setError(null);
    try {
      const uploaded = await uploadFile(`/orgs/${targetOrg}/uploads`, file, "guarantee", {}, setProgress);
      setProgress("Reading the guarantee…");
      const project = await api.post<Project>("/projects", {
        guarantee: { upload_id: uploaded.upload_id, sha256: uploaded.sha256, filename: file.name },
        ...(isAdmin ? { org_id: targetOrg } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      await client.invalidateQueries({ queryKey: keys.projects });
      navigate(`/p/${project.id}/settings?created=1`);
    } catch (reason) {
      setError(reason);
      setProgress(null);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <StageIntro
        title="Add the performance guarantee"
        description="The annual CUEP, CUE and CRTE schedule, nameplate energy and measurement boundary are read from the PDF. Everything stays editable."
        status="New project"
      />
      {isAdmin && (
        <Field label="Organisation">
          <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">Choose…</option>
            {orgs.data?.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <FileDrop
        accept="application/pdf"
        onFiles={(files) => setFile(files[0] ?? null)}
        label={
          file ? (
            <span>
              <strong>{file.name}</strong> — click to choose another file
            </span>
          ) : (
            <span>
              <strong>Drop the performance guarantee PDF here</strong>
              <br />
              <span className="muted small">or click to choose a searchable PDF</span>
            </span>
          )
        }
      />
      <Field label="Project name (optional)" hint="Pre-filled from the guarantee when left empty.">
        <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} />
      </Field>
      <ErrorNotice error={error} title="The project could not be created" />
      <div className="row end">
        {progress && <Spinner label={progress.replace(/…$/, "")} />}
        <button className="btn primary" disabled={!file || !targetOrg || !!progress} onClick={create}>
          Create project
        </button>
      </div>
    </div>
  );
}
